import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { F1065K2DataEntryService, ColumnHeaderData, LineData, FormLineItem, GroupedFormLine, ClobSettingsObj } from '../f1065-k2-data-entry.service';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { parseAsLocalDate } from '../../../../shared/_utils/date-utils';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';


@Component({
    selector: 'gtw-f1065-k2-data-entry',
    templateUrl: './f1065-k2-data-entry.component.html',
    styleUrls: ['./f1065-k2-data-entry.component.scss', '../f1065-k2.styles.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065K2DataEntryComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';
  private myScreenKey: any = null;
  private firstSetTabActiveSkipped = false;

  // Input properties from workspace context
  private baseUrl: string = '';
  private globalService: any;
  filterParams: any = {};
  private activeScreenLink: string = '';

  // Component state
  loadingData = true;
  showLoader = true;
  isAdmin = false;
  isSaveClicked = false;
  showErrCol = false;
  isValidationError = false;

  // Data
  colHeaderData: ColumnHeaderData[] = [];
  groupedFormLines: GroupedFormLine[] = [];
  
  // Dynamic configuration
  currentCodeGroup = '';
  formSchd = '';
  sectionHeader = '';
  
  // Filter tracking
  private isInitialized = false;

  // Dropdown loading
  private totalDropDownActions = 0;
  private loadedActionCounter = 0;

  // Confirmation modal
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save Data',
    message: 'Are you sure to save data?',
    iconClass: 'fa fa-save',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast'
  };

  // Labels
  currentEntityName = '';
  currentPartnerName = '';

  constructor(
    private service: F1065K2DataEntryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.myScreenKey = this.gtwWorkspace.workspaceFactory?.activeScreen?.screen_key ?? null;

    // No auto-load on tab switch: skip BehaviorSubject replay AND ignore
    // emissions for other screens (old CE getting destroyed). First load
    // happens only on Filter click (data-filters.refresh re-emits setTabActive).
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe((ws: any) => {
      if (!this.firstSetTabActiveSkipped) {
        this.firstSetTabActiveSkipped = true;
        return;
      }
      const incomingKey = ws?.screen_key ?? null;
      if (incomingKey && this.myScreenKey && incomingKey !== this.myScreenKey) {
        return;
      }
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      if (this.isInitialized) {
        this.tryInit();
      }
    }));

    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    const baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};

    this.baseUrl = baseUrl;
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.activeScreenLink = activeScreen?.link || this.activeScreenLink || '';
  }

  private tryInit(): void {
    const hasGlobalService = !!this.globalService?.globalParams;
    const hasBaseUrl = !!this.baseUrl;
    const hasFilterParams = !!this.filterParams && Object.keys(this.filterParams).length > 0;
    const hasActiveScreenLink = !!this.activeScreenLink;

    if (!hasGlobalService || !hasBaseUrl || !hasFilterParams || !hasActiveScreenLink) {
      return;
    }

    const contextKey = [
      this.baseUrl,
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      this.filterParams?.p_form || '',
      this.filterParams?.p_schedule || '',
      this.activeScreenLink
    ].join('|');

    if (contextKey === this.lastContextKey) {
      return;
    }

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  private reloadData(): void {
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.groupedFormLines = [];
    this.colHeaderData = [];
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;
    this.setCurrentCodeGroup();
    this.loadColumnHeaders();
  }

  private init(): void {
    this.setCurrentCodeGroup();
    this.checkLockStatus();
    this.loadColumnHeaders();
  }

  private setCurrentCodeGroup(): void {
    // Build code_group dynamically from filter params and activeScreen.link
    // Example: F1065_K2_P3_S5
    const pForm = this.filterParams.p_form || 'F1065';
    const pSchedule = this.filterParams.p_schedule || 'K2';
    const linkPart = this.getAfterUnderscore(this.activeScreenLink);
    
    this.currentCodeGroup = `${pForm}_${pSchedule}${linkPart}`;
    this.formSchd = `${pForm}_${pSchedule}`;
    
    // Set section header based on link part
    this.sectionHeader = this.getSectionHeader(linkPart);
  }

  private getAfterUnderscore(str: string): string {
    if (!str) return '';
    const idx = str.indexOf('_');
    return idx >= 0 ? str.substring(idx) : '';
  }

  private getSectionHeader(linkPart: string): string {
    // Map link parts to section headers
    // These can be expanded as needed
    const headers: { [key: string]: string } = {
      '_P3_S5': 'Section 5 - Other Tax Information:',
      '_P3_S6': 'Section 6 - Other Tax Information:',
      '_P3_S7': 'Section 7 - Other Tax Information:',
      '_P4_S0': 'Part IV - Information on Partners Foreign Partners:',
      '_P5_S0': 'Part V - Distributions From Foreign Corporations to Partnership:',
      '_P6_S0': 'Part VI - Information on Partners Section 951(a)(1) and Section 951A Inclusions:',
      '_P8_S0': 'Part VIII - Partners Interest in Foreign Corporation Income (Section 960):',
      '_P9_S0': 'Part IX - Partners Information for Base Erosion and Anti-Abuse Tax:',
      '_P10_S1': 'Section 1 - Interest Expense Allocation and Apportionment:',
      '_P10_S2': 'Section 2 - R&D Expense Allocation and Apportionment:'
    };
    return headers[linkPart] || `Form ${this.formSchd}${linkPart}:`;
  }

  private checkLockStatus(): void {
    this.service.getLockStatus(
      this.baseUrl,
      this.filterParams.scenario,
      this.globalService?.globalParams?.jcd_key,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any[]) => {
        if (data && data.length > 0 && data[0].LOCK_STATUS === 'Y') {
          this.isAdmin = false;
          this.service.showError(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`);
        } else {
          this.isAdmin = true;
        }
      },
      error: () => {
        this.isAdmin = true;
      }
    });
  }

  private loadColumnHeaders(): void {
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;

    this.service.getColumnHeaders(
      this.baseUrl,
      this.filterParams.tax_year,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (data: ColumnHeaderData[]) => {
          if (!data || data.length === 0) {
            this.service.showWarning('Data Error: No results were returned');
            this.loadingData = true;
            return;
          }

          this.colHeaderData = data;
          let hasDropdown = false;

          // Process column headers and check for dropdowns
          for (const col of this.colHeaderData) {
            this.setValidationInfo(col);
            if (col.DROP_DOWN_ACTION) {
              hasDropdown = true;
              this.totalDropDownActions++;
              this.loadDropdownData(col);
            }
          }

          if (!hasDropdown) {
            this.loadLineDetails();
          }
        },
        error: (err) => {
          console.error('[F1065K2-DataEntry] Error loading column headers:', err);
          this.showLoader = false;
          this.loadingData = false;
        }
      });
  }

  private setValidationInfo(obj: any): void {
    if (obj.VALIDATION_RULE) {
      const rules = obj.VALIDATION_RULE.split(';');
      for (const rule of rules) {
        if (rule === 'REQ') {
          obj.IS_MANDATORY = 'Y';
        } else if (rule.includes('MAX_LEN:')) {
          obj.MAX_LENGTH = parseInt(rule.split(':')[1], 10);
        }
      }
    }
  }

  private loadDropdownData(colData: ColumnHeaderData): void {
    const actionDetails = colData.DROP_DOWN_ACTION!.split(':');

    this.service.getDropdownData(
      this.baseUrl,
      actionDetails[0],
      actionDetails[1],
      this.filterParams.tax_year,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (data: any[]) => {
          colData.actionData = data;
          this.loadedActionCounter++;
          if (this.loadedActionCounter === this.totalDropDownActions) {
            this.loadLineDetails();
          }
        },
        error: () => {
          this.loadedActionCounter++;
          if (this.loadedActionCounter === this.totalDropDownActions) {
            this.loadLineDetails();
          }
        }
      });
  }

  private loadLineDetails(): void {
    this.showLoader = true;

    // Validate required filters
    const pFilerKey = this.filterParams.p_filer_key;
    if (!pFilerKey || (typeof pFilerKey === 'number' && pFilerKey < 0)) {
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      return;
    }

    // For K3, also validate partner key
    const isK3 = this.currentCodeGroup.includes('_K3');
    if (isK3) {
      const pFilerPartnerKey = this.filterParams.p_filer_partner_key;
      if (!pFilerPartnerKey || (typeof pFilerPartnerKey === 'number' && pFilerPartnerKey < 0)) {
        this.service.showError('Please select all the filters');
        this.showLoader = false;
        return;
      }
    }

    // Set entity and partner labels for confirmation modal
    this.currentEntityName = this.filterParams.p_filer_label || '';
    if (isK3) {
      this.currentPartnerName = this.filterParams.p_filer_partner_label || '';
    } else {
      this.currentPartnerName = '';
    }

    this.service.getLineDetails(
      this.baseUrl,
      'w5bepp',
      this.filterParams.tax_year,
      pFilerKey,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams,
      this.filterParams.p_filer_partner_key
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (data: LineData[]) => {
          this.groupedFormLines = this.processData(data);
          
          // Calculate totals on load
          for (const line of this.groupedFormLines) {
            this.calcTotalsCol(line.items);
          }

          this.showLoader = false;
          this.loadingData = false;
          this.showErrCol = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('[F1065K2-DataEntry] Error loading line details:', err);
          this.showLoader = false;
          this.loadingData = false;
        }
      });
  }

  private processData(formLines: LineData[]): GroupedFormLine[] {
    if (!formLines || formLines.length === 0) {
      return [];
    }

    this.isAdmin = true;
    const grouped: GroupedFormLine[] = [];

    for (const line of formLines) {
      let bucket = grouped.find(g =>
        g.SCHD_KEY === line.SCHD_KEY &&
        g.LINE_KEY === line.LINE_KEY &&
        g.LINE_NO === line.LINE_NO &&
        g.SNO === line.SNO
      );

      if (!bucket) {
        bucket = {
          SCHD_KEY: line.SCHD_KEY,
          LINE_KEY: line.LINE_KEY,
          LINE_NO: line.LINE_NO,
          LINE_DESC: line.LINE_DESC,
          LINE_ORDER: line.LINE_ORDER,
          IS_DISPLAY: 'Y',
          SNO: line.SNO,
          IS_LINE_MULTI: line.IS_LINE_MULTI,
          items: []
        };
        grouped.push(bucket);
      }

      const item: FormLineItem = {
        SCHD_KEY: line.SCHD_KEY,
        LINE_KEY: line.LINE_KEY,
        COL_KEY: line.COL_KEY,
        COL_DESC: line.COL_DESC,
        COL_ORDER: line.COL_ORDER,
        IS_ENABLED: line.EDITABLE_Y_N,
        SNO: line.SNO,
        ADJ_KEY: line.ADJ_KEY,
        IS_COL_MULTI: line.IS_COL_MULTI,
        UI_COMP: line.UI_COMP,
        ACTION_Y_N: line.ACTION_Y_N,
        IS_MANDATORY: 'N',
        CELL_VALUE: line.CELL_VALUE
      };

      // Assign dropdown data if needed
      if (item.ACTION_Y_N === 'Y') {
        const colObj = this.colHeaderData.find(c => c.COL_KEY === item.COL_KEY);
        if (colObj) {
          item.actionData = colObj.actionData;
        }
      }

      this.setValidationInfo(item);

      // Set operation type
      if (item.ADJ_KEY != null) {
        item.OPERATION_TYPE = 'U';
      } else {
        item.OPERATION_TYPE = undefined;
      }

      // Initialize empty values for multi-line rows
      if (line.SNO === 0 && line.IS_LINE_MULTI === 'Y' && item.CELL_VALUE == null) {
        item.CELL_VALUE = '';
      }

      // Handle date values
      if (item.UI_COMP === 'DATE') {
        if (item.CELL_VALUE && item.CELL_VALUE !== 0) {
          item.CELL_VALUE = new Date(line.CELL_VALUE as string);
        } else {
          item.CELL_VALUE = '';
        }
      }

      // Mark disabled cells as not saveable
      if (item.IS_ENABLED === 'N') {
        item.OPERATION_TYPE = 'N';
      }

      bucket.items.push(item);
    }

    // Sort items in each bucket by COL_ORDER
    for (const bucket of grouped) {
      bucket.items.sort((a, b) => (a.COL_ORDER || 0) - (b.COL_ORDER || 0));
    }

    // Sort grouped lines
    grouped.sort((a, b) => {
      if (a.LINE_ORDER !== b.LINE_ORDER) {
        return (a.LINE_ORDER || 0) - (b.LINE_ORDER || 0);
      }
      return (a.SNO || 0) - (b.SNO || 0);
    });

    return grouped;
  }

  // ============================================
  // PUBLIC METHODS
  // ============================================

  /**
   * Handle keypress for NUMERIC inputs - only allow digits and minus at start
   */
  onNumericKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;

    // Allow digits 0-9
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Allow minus sign only at the start (position 0) and if not already present
    if (charStr === '-') {
      if (selectionStart === 0 && !currentValue.includes('-')) {
        return true;
      }
      return false;
    }

    // Block all other characters (including decimal point)
    return false;
  }

  /**
   * Handle paste for NUMERIC inputs - filter to only allowed characters
   */
  onNumericPaste(event: ClipboardEvent, item: FormLineItem): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let filtered = pastedText.replace(/[^0-9-]/g, '');
    if (filtered.includes('-')) {
      const hasMinusAtStart = filtered.startsWith('-');
      filtered = filtered.replace(/-/g, '');
      if (hasMinusAtStart) {
        filtered = '-' + filtered;
      }
    }
    item.CELL_VALUE = filtered ? parseInt(filtered, 10) : null;
    this.cdr.detectChanges();
  }

  calcTotalsCol(columnValues: FormLineItem[]): void {
    if (!columnValues || columnValues.length < 2) return;

    const lastColumn = columnValues[columnValues.length - 1];
    
    // Only calculate if last column (Total) is enabled
    if (lastColumn.IS_ENABLED !== 'Y') return;

    // Match original AngularJS behavior exactly:
    // Always set totalValue (even if 0), not null
    let totalValue = 0;

    for (let i = 0; i < columnValues.length - 1; i++) {
      if (columnValues[i].UI_COMP === 'NUMERIC' &&
          (columnValues[i].CELL_VALUE != null || columnValues[i].CELL_VALUE != undefined)) {
        totalValue = totalValue + +(columnValues[i].CELL_VALUE || 0);
      }
    }

    // Always set totalValue (matching original: even 0 is saved)
    lastColumn.CELL_VALUE = totalValue;
  }

  /**
   * Check if item is the last NUMERIC column (Total column - should be readonly)
   */
  isLastNumericColumn(items: FormLineItem[], item: FormLineItem): boolean {
    if (!items || items.length === 0) return false;
    
    const numericItems = items.filter(i => i.UI_COMP === 'NUMERIC');
    if (numericItems.length === 0) return false;
    
    const lastNumeric = numericItems[numericItems.length - 1];
    return item.COL_KEY === lastNumeric.COL_KEY;
  }

  // Helper method for date input changes - uses parseAsLocalDate to avoid timezone shift
  onDateChange(item: FormLineItem, dateString: string): void {
    item.CELL_VALUE = dateString ? parseAsLocalDate(dateString) : null;
  }

  addRow(selectedRowData: GroupedFormLine): void {
    const index = this.groupedFormLines.indexOf(selectedRowData);
    const multiRows = this.groupedFormLines.filter(
      g => g.LINE_KEY === selectedRowData.LINE_KEY && g.IS_DISPLAY === 'Y'
    );
    const sno = multiRows.length > 0 ? multiRows[multiRows.length - 1].SNO + 1 : 1;

    const newRow: GroupedFormLine = {
      SCHD_KEY: selectedRowData.SCHD_KEY,
      LINE_KEY: selectedRowData.LINE_KEY,
      LINE_NO: '',
      LINE_DESC: '',
      LINE_ORDER: selectedRowData.LINE_ORDER,
      IS_DISPLAY: 'Y',
      SNO: sno,
      IS_LINE_MULTI: selectedRowData.IS_LINE_MULTI,
      items: selectedRowData.items.map(item => ({
        ...item,
        OPERATION_TYPE: 'I',
        ADJ_KEY: undefined,
        CELL_VALUE: item.UI_COMP === 'NUMERIC' ? 0 : '',
        SNO: sno
      }))
    };

    this.groupedFormLines.splice(index + sno, 0, newRow);
    this.cdr.detectChanges();
  }

  deleteRow(selectedRowData: GroupedFormLine): void {
    let isRowSaved = false;
    for (const item of selectedRowData.items) {
      if (item.OPERATION_TYPE === 'U') {
        item.OPERATION_TYPE = 'D';
        selectedRowData.IS_DISPLAY = 'N';
        isRowSaved = true;
      }
    }
    if (!isRowSaved) {
      const index = this.groupedFormLines.indexOf(selectedRowData);
      this.groupedFormLines.splice(index, 1);
    }
    this.cdr.detectChanges();
  }

  reset(): void {
    // Clear array before reload to prevent rendering stale data during async fetch
    this.groupedFormLines = [];
    this.showLoader = true;
    this.cdr.detectChanges();
    
    this.loadLineDetails();
  }

  save(): void {
    this.isSaveClicked = true;
    this.validateForm();

    if (this.isValidationError) {
      this.service.showError('Please fix all error(s)');
      this.isSaveClicked = false;
      return;
    }

    // Show confirmation modal
    this.confirmModalConfig.message = `Are you sure to save data for: ${this.currentEntityName}`;
    if (this.currentPartnerName) {
      this.confirmModalConfig.message += ` / ${this.currentPartnerName}`;
    }
    this.showConfirmModal = true;
  }

  onSaveConfirm(): void {
    this.showConfirmModal = false;
    this.performSave();
  }

  onSaveCancel(): void {
    this.showConfirmModal = false;
    this.isSaveClicked = false;
  }

  private validateForm(): void {
    this.isValidationError = false;

    for (const line of this.groupedFormLines) {
      line.ERR_MSG = undefined;

      if (line.IS_DISPLAY !== 'Y') continue;

      for (const item of line.items) {
        item.IS_ERROR = 'N';

        if (item.IS_ENABLED === 'N') continue;

        // Mandatory validation
        if (item.IS_MANDATORY === 'Y' && this.isEmpty(item) && this.isAnyOtherValueEntered(item, line.items)) {
          this.setErrorAttribs(line, item, 'Mandatory field(s) are missing');
        }

        // Date validation (if dates are used)
        if (item.UI_COMP === 'DATE' && item.CELL_VALUE) {
          const dateVal = item.CELL_VALUE as Date;
          if (dateVal && dateVal.getFullYear && dateVal.getFullYear() >= 2022) {
            this.setErrorAttribs(line, item, 'Date Tax Paid should be before 2022 year');
          }
        }
      }
    }
  }

  private isEmpty(item: FormLineItem): boolean {
    return item.CELL_VALUE == null || item.CELL_VALUE === '' || item.CELL_VALUE === 0;
  }

  private isAnyOtherValueEntered(item: FormLineItem, items: FormLineItem[]): boolean {
    return items.some(x => x !== item && x.IS_ENABLED === 'Y' && !this.isEmpty(x));
  }

  private setErrorAttribs(line: GroupedFormLine, col: FormLineItem, errMsg: string): void {
    if (!line.ERR_MSG) {
      line.ERR_MSG = '<ol></ol>';
    }
    col.IS_ERROR = 'Y';
    this.isValidationError = true;
    line.ERR_MSG = line.ERR_MSG.replace('</ol>', `<li>${errMsg}</li></ol>`);
    this.showErrCol = true;
  }

  private performSave(): void {
    const dataToSave = this.buildDataToSave();

    const settingsObj: ClobSettingsObj = {
      jcd_key: this.globalService?.globalParams?.jcd_key,
      p_form_schd: this.formSchd,
      p_code_group: this.currentCodeGroup
    };

    // Call getLockStatus before save (matching original AngularJS behavior)
    this.service.getLockStatus(
      this.baseUrl,
      this.filterParams.tax_year || '',
      this.globalService?.globalParams?.jcd_key,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Proceed with save after lock status check
        this.service.saveData(this.baseUrl, settingsObj, dataToSave, this.globalService?.globalParams || {}, this.filterParams, this.globalService?.inputs?.tax_year?.values || []).pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (result: any) => {
              // Use loose comparison (!=) like original AngularJS: result.data.callSuccess != 1
              if (result.callSuccess != 1) {
                // Handle errorMessage: "null" string from API
                const errorMsg = (result?.errorMessage && result.errorMessage !== 'null') 
                  ? result.errorMessage 
                  : 'Save failed';
                this.service.showError(errorMsg);
              } else {
                this.service.showSuccess('Data have been saved/updated');
              }
              this.isSaveClicked = false;
              this.showErrCol = false;
            },
            error: (err) => {
              console.error('[F1065K2-DataEntry] Save error:', err);
              this.service.showError('An error occurred while saving');
              this.isSaveClicked = false;
            }
          });
      },
      error: (err) => {
        console.error('[F1065K2-DataEntry] Lock status check error:', err);
        this.isSaveClicked = false;
      }
    });
  }

  private buildDataToSave(): any[] {
    const dataToSave: any[] = [];

    for (const line of this.groupedFormLines) {
      for (const item of line.items) {
        const returnObj: any = {
          SCHD_KEY: item.SCHD_KEY,
          LINE_KEY: item.LINE_KEY,
          COL_KEY: item.COL_KEY,
          SNO: item.SNO
        };

        // Handle CELL_VALUE based on UI_COMP/LINE_COMP type
        const itemAny = item as any;
        const comp = itemAny.LINE_COMP || item.UI_COMP;
        
        if (comp === 'DATE') {
          // Format date as YYYY-M-D (without leading zeros) to match original AngularJS
          if (item.CELL_VALUE instanceof Date) {
            const d = item.CELL_VALUE as Date;
            returnObj.CELL_VALUE = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
          } else if (item.CELL_VALUE && typeof item.CELL_VALUE === 'string') {
            // If stored as string YYYY-MM-DD, convert to YYYY-M-D
            const parts = item.CELL_VALUE.split('-');
            if (parts.length === 3) {
              returnObj.CELL_VALUE = `${parseInt(parts[0])}-${parseInt(parts[1])}-${parseInt(parts[2])}`;
            } else {
              returnObj.CELL_VALUE = item.CELL_VALUE;
            }
          } else {
            returnObj.CELL_VALUE = '';
          }
        } else if (comp === 'NUMERIC') {
          // NUMERIC fields: check if this is the Total column (string with decimals like "1244.0000")
          if (item.CELL_VALUE !== null && item.CELL_VALUE !== undefined && item.CELL_VALUE !== '') {
            if (typeof item.CELL_VALUE === 'string' && item.CELL_VALUE.includes('.') && /\.\d{4}$/.test(item.CELL_VALUE)) {
              // Keep as string for Total column
              returnObj.CELL_VALUE = item.CELL_VALUE;
            } else {
              // Convert to number for regular NUMERIC fields
              returnObj.CELL_VALUE = Number(item.CELL_VALUE);
            }
          } else {
            returnObj.CELL_VALUE = null;
          }
        } else if (comp === 'CHK_BX') {
          // Checkboxes: send "X" if checked, "" if not (matching original AngularJS)
          returnObj.CELL_VALUE = item.CELL_VALUE === true || item.CELL_VALUE === 'X' || item.CELL_VALUE === '1' ? 'X' : '';
        } else if (comp === 'DROP_DOWN') {
          // Dropdowns: empty value should be null (matching original AngularJS)
          if (item.CELL_VALUE === null || item.CELL_VALUE === undefined || item.CELL_VALUE === '') {
            returnObj.CELL_VALUE = null;
          } else {
            returnObj.CELL_VALUE = item.CELL_VALUE;
          }
        } else {
          returnObj.CELL_VALUE = item.CELL_VALUE;
        }

        // Always include ADJ_KEY (matching original AngularJS behavior)
        returnObj.ADJ_KEY = item.ADJ_KEY;

        // Check if item has a real value (not null, undefined, or empty string)
        const hasValue = item.CELL_VALUE != null && item.CELL_VALUE !== '' && item.CELL_VALUE !== undefined;

        // FIX: Match original AngularJS pattern (P7S2, P12, P11 controllers)
        // Set OPERATION_TYPE UNCONDITIONALLY first, then handle special cases
        // This ensures items with cleared values (OPERATION_TYPE='U' but empty value) are still sent to API
        returnObj.OPERATION_TYPE = item.OPERATION_TYPE;

        if (line.IS_LINE_MULTI === 'Y') {
          if (hasValue && item.OPERATION_TYPE == null) {
            returnObj.OPERATION_TYPE = 'I';
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'I') {
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'N') {
            returnObj.OPERATION_TYPE = null;
          }
        } else {
          if (item.OPERATION_TYPE == null && hasValue) {
            // New value being inserted
            returnObj.OPERATION_TYPE = 'I';
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'I') {
            // After insert, mark as update for future saves
            item.OPERATION_TYPE = 'U';
          } else if (item.OPERATION_TYPE === 'D') {
            // Deletion
            returnObj.OPERATION_TYPE = 'D';
            item.OPERATION_TYPE = undefined;
          } else if (item.OPERATION_TYPE === 'N') {
            // Disabled/not saveable
            returnObj.OPERATION_TYPE = null;
          }
          // Note: When OPERATION_TYPE is 'U', it stays 'U' (set unconditionally above)
          // This allows clearing values to be sent to API
        }

        // Only include items with valid OPERATION_TYPE
        if (returnObj.OPERATION_TYPE != null && returnObj.OPERATION_TYPE !== '') {
          dataToSave.push(returnObj);
        }
      }
    }

    return dataToSave;
  }

  // Track by functions for ngFor
  trackByRow(index: number, row: GroupedFormLine): string {
    return `${row.LINE_KEY}_${row.SNO}_${row.IS_DISPLAY}`;
  }

  trackByCol(index: number, col: FormLineItem): string {
    return `${col.COL_KEY}_${col.SNO}`;
  }
}
